/**
 * Freemium license gate.
 *
 * Everything runs locally, so a "license" is a signed-ish activation record in
 * localStorage. It is not meant to be unhackable — it is meant to be a clear,
 * honest product boundary: free recordings are capped at 45 seconds and carry a
 * small badge, Pro unlocks unlimited length, 1080p/4K bitrates, custom brand
 * watermarks and watermark removal.
 */

export type LicenseTier = 'free' | 'pro'
export type LicensePlan = 'monthly' | 'lifetime'

export interface License {
  tier: LicenseTier
  plan: LicensePlan | null
  key: string | null
  activatedAt: number | null
  /** Epoch ms, `null` for lifetime. */
  expiresAt: number | null
}

/** Free tier ceiling for a single recording, in seconds. */
export const FREE_LIMIT_SECONDS = 45
/** Free tier export ceiling (the studio trims/limits beyond this). */
export const FREE_MAX_EXPORT_SECONDS = 45

export const PRO_PRICING = {
  monthly: { amount: 9, cadence: 'month' as const },
  lifetime: { amount: 29, cadence: 'once' as const },
}

/** How long a monthly license stays valid after activation. */
export const MONTHLY_PERIOD_MS = 31 * 24 * 60 * 60 * 1000

const STORAGE_KEY = 'demopolish.license.v1'
const KEY_PATTERN = /^(?:DP-|DPRO-|LIFE-)[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/

export function createFreeLicense(): License {
  return { tier: 'free', plan: null, key: null, activatedAt: null, expiresAt: null }
}

export function isLicenseKeyShapeValid(key: string): boolean {
  return KEY_PATTERN.test(key.trim().toUpperCase())
}

export interface ActivationResult {
  ok: boolean
  license: License
  error?: string
}

/**
 * Activate a key. Keys starting with `LIFE` are treated as lifetime licenses,
 * everything else monthly. Returns the resulting license so callers can test
 * the outcome without touching storage.
 */
export function activateLicense(key: string, plan: LicensePlan, now = Date.now()): ActivationResult {
  const normalized = key.trim().toUpperCase()
  if (!normalized) {
    return { ok: false, license: createFreeLicense(), error: 'Enter a license key.' }
  }
  if (!isLicenseKeyShapeValid(normalized)) {
    return {
      ok: false,
      license: createFreeLicense(),
      error: 'That key does not look right. Use DP-XXXX-XXXX-XXXX (or LIFE-XXXX-XXXX-XXXX).',
    }
  }
  const lifetime = plan === 'lifetime' || normalized.startsWith('LIFE')
  const license: License = {
    tier: 'pro',
    plan: lifetime ? 'lifetime' : 'monthly',
    key: normalized,
    activatedAt: now,
    expiresAt: lifetime ? null : now + MONTHLY_PERIOD_MS,
  }
  return { ok: true, license }
}

/** True when the license currently grants Pro (never expires for lifetime). */
export function isPro(license: License, now = Date.now()): boolean {
  if (license.tier !== 'pro') return false
  if (!license.expiresAt) return true
  return license.expiresAt > now
}

/** Seconds a recording may run before the free tier cuts it off. */
export function maxRecordingSeconds(license: License, now = Date.now()): number {
  return isPro(license, now) ? Number.POSITIVE_INFINITY : FREE_LIMIT_SECONDS
}

/** Highest export quality the license allows. */
export function maxExportSeconds(license: License, now = Date.now()): number {
  return isPro(license, now) ? Number.POSITIVE_INFINITY : FREE_MAX_EXPORT_SECONDS
}

export function secondsRemaining(license: License, elapsed: number, now = Date.now()): number | null {
  if (isPro(license, now)) return null
  return Math.max(0, FREE_LIMIT_SECONDS - Math.max(0, elapsed))
}

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function memoryStorage(): StorageLike {
  const map = new Map<string, string>()
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => void map.set(key, value),
    removeItem: (key) => void map.delete(key),
  }
}

/** Browser storage when available, an in-memory shim otherwise (SSR/tests). */
export function resolveStorage(storage?: StorageLike | null): StorageLike {
  if (storage) return storage
  try {
    if (typeof localStorage !== 'undefined') return localStorage
  } catch {
    /* access can throw in private modes */
  }
  return memoryStorage()
}

export function loadLicense(storage?: StorageLike | null): License {
  const store = resolveStorage(storage)
  try {
    const raw = store.getItem(STORAGE_KEY)
    if (!raw) return createFreeLicense()
    const parsed = JSON.parse(raw) as Partial<License>
    if (typeof parsed !== 'object' || parsed === null) return createFreeLicense()
    if (parsed.tier === 'pro' && typeof parsed.key === 'string') {
      return {
        tier: 'pro',
        plan: parsed.plan === 'lifetime' || parsed.plan === 'monthly' ? parsed.plan : 'monthly',
        key: parsed.key,
        activatedAt: typeof parsed.activatedAt === 'number' ? parsed.activatedAt : null,
        expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : null,
      }
    }
    return createFreeLicense()
  } catch {
    return createFreeLicense()
  }
}

export function saveLicense(license: License, storage?: StorageLike | null): void {
  const store = resolveStorage(storage)
  try {
    if (license.tier === 'pro') store.setItem(STORAGE_KEY, JSON.stringify(license))
    else store.removeItem(STORAGE_KEY)
  } catch {
    /* storage full / blocked — the app still works as free */
  }
}

export function clearLicense(storage?: StorageLike | null): void {
  resolveStorage(storage).removeItem(STORAGE_KEY)
}

/** How many days are left on a monthly license, `null` for lifetime/expired. */
export function daysRemaining(license: License, now = Date.now()): number | null {
  if (!isPro(license, now) || license.expiresAt === null) return null
  return Math.max(0, Math.ceil((license.expiresAt - now) / (24 * 60 * 60 * 1000)))
}
